import { listOrgEnrollments } from "./enrollmentHelpers"
import { listOrgLearningEvents } from "./learningEventHelpers"
import { listOrgCertificates } from "./certHelpers"
import { buildBuckets } from "./dateRangeHelpers"

const toMillis = (value) => {
  if (!value) return null
  if (typeof value?.toMillis === "function") return value.toMillis()
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

// Returns { value, label } per bucket — label feeds the bar's hover tooltip.
const dailySeries = (timestamps, buckets) =>
  buckets?.map((bucket) => ({
    value:
      timestamps?.filter((ts) => {
        const ms = toMillis(ts)
        return ms !== null && ms >= bucket.start.getTime() && ms <= bucket.end.getTime()
      })?.length || 0,
    label: bucket?.label,
  }))

const flattenAssessmentTimestamps = (enrollments) => {
  const timestamps = []
  enrollments?.forEach((enrollment) => {
    Object.values(enrollment?.moduleQuizResults || {})?.forEach((result) => {
      if (result?.attemptCount) timestamps.push(result?.lastAttemptAt)
    })
    if (enrollment?.capstoneResult?.attemptCount) {
      timestamps.push(enrollment?.capstoneResult?.lastAttemptAt)
    }
  })
  return timestamps
}

export const computeProofOfLearning = async (orgId, rangeKey = "30d") => {
  const [{ enrollments }, { events }, { certificates }] = await Promise.all([
    listOrgEnrollments(orgId),
    listOrgLearningEvents(orgId),
    listOrgCertificates(orgId),
  ])

  const buckets = buildBuckets(rangeKey)
  const assessmentTimestamps = flattenAssessmentTimestamps(enrollments)

  return {
    learningRecords: {
      total: enrollments?.length || 0,
      data: dailySeries(enrollments?.map((e) => e?.enrolledAt), buckets),
    },
    achievementsRecorded: {
      total: events?.length || 0,
      data: dailySeries(events?.map((e) => e?.createdAt), buckets),
    },
    assessmentEvidence: {
      total: assessmentTimestamps?.length || 0,
      data: dailySeries(assessmentTimestamps, buckets),
    },
    credentialEvidence: {
      total: certificates?.length || 0,
      data: dailySeries(certificates?.map((c) => c?.issueDate), buckets),
    },
  }
}

// Per-learner evidence chain (Course → Assessments → Credential) for the
// Proof of Learning drill-down. Pure/synchronous on purpose — it operates on
// data the caller already fetched (listOrgLearners + listOrgCertificates),
// rather than re-querying Firestore per learner selected in the UI.
export const buildLearnerEvidenceChain = (learner, certificates) => {
  if (!learner) return []
  const learnerCerts = certificates?.filter((c) => c?.userId === learner.userId) || []

  return (
    learner.enrollments?.map((enrollment, i) => {
      const moduleAssessments = Object.entries(enrollment?.moduleQuizResults || {}).map(
        ([key, result]) => ({
          key,
          type: "module",
          score: result?.bestScore ?? result?.score ?? null,
          attempts: result?.attemptCount || 0,
          lastAttemptAt: result?.lastAttemptAt || null,
          passed: result?.passed ?? null,
        })
      )

      const capstone = enrollment?.capstoneResult?.attemptCount
        ? [
            {
              key: "capstone",
              type: "capstone",
              score: enrollment.capstoneResult?.score ?? null,
              attempts: enrollment.capstoneResult?.attemptCount || 0,
              lastAttemptAt: enrollment.capstoneResult?.lastAttemptAt || null,
              passed: enrollment.capstoneResult?.passed ?? null,
            },
          ]
        : []

      const credential = learnerCerts.find((c) => c?.courseId === enrollment?.courseId) || null

      return {
        courseId: enrollment?.courseId,
        courseTitle: learner.courseTitles?.[i] || "Untitled Course",
        progressPercent: enrollment?.progressPercent || 0,
        completed: (enrollment?.progressPercent || 0) >= 100,
        assessments: [...moduleAssessments, ...capstone],
        credential,
      }
    }) || []
  )
}